/*
 * TOP TEN GB — German (Budoland) pre-order, storefront side.
 *
 * ONE rule, mirrored in three places — keep them identical:
 *   apps/ingestion/lib/de_preorder.py  resolve_state()
 *   snippets/de-preorder-state.liquid   (server render / no-JS)
 *   this file                           resolveState()
 * scripts/verify_de_preorder.js runs the shared vectors against this file.
 *
 * Runs on top of Clean Canvas "Enterprise": the theme's variant-picker sets the
 * add-to-cart button text/disabled on every change, THEN dispatches
 * `on:variant:change` — we listen to that and overrule where our state says so.
 * In the "uk" state we hand the button back to the theme untouched.
 */
(function () {
  var STATE_UK = 'uk';
  var STATE_PREORDER = 'preorder';
  var STATE_INBOUND = 'inbound';
  var STATE_SOLD_OUT = 'sold_out';

  function toInt(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? 0 : n;
  }

  /**
   * THE rule (see de_preorder.py resolve_state).
   * @param {number} total       variant.inventory_quantity (all locations)
   * @param {number} de          published German qty (topten.de_qty)
   * @param {number} deShopify   units the sync placed at the German Shopify location
   * @param {boolean} etaUsable  a restock date exists and is today or later
   * @param {boolean} fresh      feed freshness guard passed
   * @param {boolean} enabled    global AND per-product switches allow pre-orders
   */
  function resolveState(total, de, deShopify, etaUsable, fresh, enabled) {
    var uk = toInt(total) - toInt(deShopify);
    if (uk > 0) return STATE_UK;
    if (fresh && enabled && toInt(de) > 0) return STATE_PREORDER;
    if (fresh && etaUsable) return STATE_INBOUND;
    return STATE_SOLD_OUT;
  }

  /**
   * A pre-order can only be offered if Shopify will actually accept the add to
   * cart (German location stocked — Option A — or inventory policy "continue" —
   * Option B). Until then the honest state is inbound/sold out.
   */
  function applyAvailability(state, available, etaUsable, fresh) {
    if (state === STATE_PREORDER && !available) {
      return (fresh && etaUsable) ? STATE_INBOUND : STATE_SOLD_OUT;
    }
    return state;
  }

  function isoToday() {
    var d = new Date();
    return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
  }

  function etaUsable(etaIso) {
    return !!etaIso && etaIso >= isoToday();   // ISO strings compare lexically
  }

  function longDate(d) {
    try {
      return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
    } catch (e) {
      return d.toDateString();
    }
  }

  function expectedBy(leadDays) {
    var d = new Date();
    d.setDate(d.getDate() + (toInt(leadDays) || 21));
    return d;
  }

  function isoToDate(iso) {
    var p = (iso || '').split('-');
    return new Date(toInt(p[0]), toInt(p[1]) - 1, toInt(p[2]));
  }

  // Export for the node test + for other scripts on the page.
  var api = { resolveState: resolveState, applyAvailability: applyAvailability, etaUsable: etaUsable };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.TopTenDePreorder = api;
  if (typeof customElements === 'undefined' || customElements.get('de-preorder')) return;

  class DePreorder extends HTMLElement {
    connectedCallback() {
      var dataEl = this.querySelector('script[type="application/json"]');
      if (!dataEl) return;
      try {
        this.data = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }
      this.section = this.closest('.js-product') || this.closest('.product-info') || document;
      this.form = this.closest('form');
      this.note = this.querySelector('.de-preorder__note');
      this.propInputs = Array.prototype.slice.call(this.querySelectorAll('input[data-de-prop]'));
      this.btn = this.form ? this.form.querySelector('[name="add"]') : null;
      if (!this.btn) return;
      this.originalAtcText = this.btn.dataset.addToCartText || this.btn.textContent.trim();
      this.state = null;
      this.applying = false;

      var picker = this.section.querySelector('variant-picker');
      if (picker) {
        picker.addEventListener('on:variant:change', function (evt) {
          this.apply(evt.detail && evt.detail.variant ? evt.detail.variant.id : null);
        }.bind(this));
      }

      // Enterprise defers its own first updateAddToCartButton() with setTimeout —
      // run after it so our label wins on first paint too.
      setTimeout(function () { this.apply(this.currentVariantId()); }.bind(this), 60);

      // Belt and braces: if anything else rewrites the button while we are in a
      // non-UK state, put our label back.
      this.observer = new MutationObserver(function () {
        if (this.applying || !this.state || this.state === STATE_UK) return;
        this.applying = true;
        this.paintButton(this.state);
        this.applying = false;
      }.bind(this));
      this.observer.observe(this.btn, { childList: true, characterData: true, subtree: true, attributes: true, attributeFilter: ['disabled'] });
    }

    currentVariantId() {
      var input = this.form ? this.form.querySelector('[name="id"]') : null;
      return input ? input.value : null;
    }

    stateFor(variantId) {
      var v = variantId ? this.data.variants[String(variantId)] : null;
      if (!v) return { state: null, variant: null };
      if (v.tracked === false) return { state: STATE_UK, variant: v, etaUsable: false };   // untracked = always sells
      var eta = etaUsable(v.eta);
      var state = resolveState(v.total, v.de, v.deShopify, eta, !!this.data.fresh, !!this.data.enabled);
      state = applyAvailability(state, !!v.available, eta, !!this.data.fresh);
      return { state: state, variant: v, etaUsable: eta };
    }

    apply(variantId) {
      var r = this.stateFor(variantId);
      this.applying = true;
      this.state = r.state;
      this.setAttribute('data-de-state', r.state || 'none');
      if (!r.state) {
        // No variant selected (Enterprise "pre-selection" mode) — stay out of the way.
        this.toggleProps(false);
        this.showNote('');
        this.toggleThemeBits(false);
        this.applying = false;
        return;
      }
      this.paintButton(r.state);

      var s = this.data.strings;
      if (r.state === STATE_PREORDER) {
        var by = longDate(expectedBy(r.variant.lead));
        this.propInputs.forEach(function (i) {
          if (i.dataset.deProp === 'dispatch') i.value = s.dispatchProperty.replace('[date]', by);
        });
        this.toggleProps(true);
        this.showNote(s.notePreorder.replace('[date]', by), STATE_PREORDER);
        this.toggleThemeBits(true);
      } else if (r.state === STATE_INBOUND) {
        this.toggleProps(false);
        this.showNote(s.noteInbound.replace('[date]', longDate(isoToDate(r.variant.eta))), STATE_INBOUND);
        this.toggleThemeBits(true);
      } else if (r.state === STATE_SOLD_OUT) {
        this.toggleProps(false);
        this.showNote(s.noteSoldOut || '', STATE_SOLD_OUT);
        this.toggleThemeBits(true);
      } else {
        this.toggleProps(false);
        this.showNote(s.noteUk || '', STATE_UK);
        this.toggleThemeBits(false);
      }
      this.applying = false;
    }

    paintButton(state) {
      var s = this.data.strings;
      if (state === STATE_UK) {
        // Hand the button back to the theme: restore its label, keep its own disabled logic.
        this.btn.dataset.addToCartText = this.originalAtcText;
        if (!this.btn.disabled && this.btn.textContent.trim() === s.buttonPreorder) {
          this.btn.textContent = this.originalAtcText;
        }
        this.btn.classList.remove('btn--de-preorder');
      } else if (state === STATE_PREORDER) {
        this.btn.dataset.addToCartText = s.buttonPreorder;
        this.btn.disabled = false;
        this.btn.removeAttribute('aria-disabled');
        if (this.btn.textContent.trim() !== s.buttonPreorder) this.btn.textContent = s.buttonPreorder;
        this.btn.classList.add('btn--de-preorder');
      } else {
        this.btn.dataset.addToCartText = this.originalAtcText;
        this.btn.disabled = true;
        if (this.btn.textContent.trim() !== s.buttonSoldOut) this.btn.textContent = s.buttonSoldOut;
        this.btn.classList.remove('btn--de-preorder');
      }
    }

    toggleProps(on) {
      this.propInputs.forEach(function (i) { i.disabled = !on; });
    }

    showNote(text, state) {
      if (!this.note) return;
      this.note.textContent = text || '';
      this.note.hidden = !text;
      this.note.setAttribute('data-de-state', state || '');
    }

    /** Hide the theme's own stock/backorder/express-checkout bits when we are
     * telling a different story (pre-order, inbound, sold out). */
    toggleThemeBits(hide) {
      var sel = ['product-inventory', '.product-backorder', '.product-info__dynamic-checkout-button'];
      sel.forEach(function (q) {
        this.section.querySelectorAll(q).forEach(function (el) {
          if (hide) {
            if (!el.hasAttribute('data-de-hidden')) {
              el.setAttribute('data-de-hidden', el.hidden ? '1' : '0');
              el.hidden = true;
            }
          } else if (el.hasAttribute('data-de-hidden')) {
            el.hidden = el.getAttribute('data-de-hidden') === '1';
            el.removeAttribute('data-de-hidden');
          }
        });
      }.bind(this));
    }
  }

  customElements.define('de-preorder', DePreorder);
})();
